import { Box, Group } from '@mantine/core';
import { useState, useEffect } from 'react';

interface ColorPickerProps {
  currentColor: string;
  onColorChange: (color: string) => void;
  size?: 'sm' | 'md' | 'lg';
}

// Predefined color palette inspired by post-it notes
const NOTE_COLORS = [
  '#FFE066', // Classic yellow
  '#FFB3BA', // Light pink
  '#BAFFC9', // Light green
  '#BAE1FF', // Light blue
  '#FFFFBA', // Pale yellow
  '#E1BAFF', // Light purple
  '#FFDFBA', // Light orange
  '#FFB3E6', // Bright pink
  '#B3FFB3', // Bright green
  '#FFE0B3'  // Peach
];

export const ColorPicker = ({ currentColor, onColorChange, size = 'md' }: ColorPickerProps) => {
  const [selectedColor, setSelectedColor] = useState(currentColor);

  useEffect(() => {
    setSelectedColor(currentColor);
  }, [currentColor]);

  const handleColorClick = (color: string) => {
    setSelectedColor(color);
    onColorChange(color);
  };

  const getColorSize = () => {
    switch (size) {
      case 'sm': return 20;
      case 'lg': return 36;
      default: return 28;
    }
  };

  const colorSize = getColorSize();

  return (
    <Group gap="xs" wrap="nowrap">
      {NOTE_COLORS.map((color) => (
        <Box
          key={color}
          onClick={() => handleColorClick(color)}
          style={{
            width: colorSize,
            height: colorSize,
            backgroundColor: color,
            borderRadius: '50%',
            cursor: 'pointer',
            border: selectedColor === color ? '3px solid var(--app-text-primary)' : '2px solid var(--app-border)',
            transition: 'all 0.2s ease',
            flexShrink: 0,
            boxShadow: selectedColor === color 
              ? '0 0 0 2px var(--app-bg), 0 2px 8px rgba(0, 0, 0, 0.15)'
              : '0 1px 3px rgba(0, 0, 0, 0.12)'
          }}
          onMouseEnter={(e) => {
            if (selectedColor !== color) {
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.2)';
            }
          }}
          onMouseLeave={(e) => {
            if (selectedColor !== color) {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.12)';
            }
          }}
        />
      ))}
    </Group>
  );
};

export default ColorPicker;